import { ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';
import Header from '../../common/Header/Header';
import {
  Buttons,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import Table from '../../components/Table/Table';
import { useGetProducts } from '../../services/product/product.service';
import { Product } from '../../services/product/product.types';
import { useFetchErrorToast } from '../../utils/useFetchErrorToasts/useFetchErrorToast';
import Divider from '@mui/material/Divider';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const Products = () => {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const [selectedRow, setSelectedRow] = useState<Product | undefined>(undefined);
  const { data, isError: productsError } = useGetProducts();
  const muiTheme = createTheme();

  const allProducts = data?.data || [];

  const handleRowSelection = () => {
    const selectedRows = gridRef?.current?.api.getSelectedRows();
    setSelectedRow(selectedRows?.[0]);
  };

  const handleOpenClick = () => {
    navigate(`/tables/products/${selectedRow?.classNo}/${selectedRow?.productNo}`);
  };

  useFetchErrorToast([{ isError: productsError, label: 'Error fetching products', id: 'productFetchError' }]);

  const headers = [
    { label: 'MOA Class No.', key: 'classNo' },
    { label: 'MOA Product No.', key: 'productNo' },
    { label: 'Sequence No.', key: 'seqNo' },
    { label: 'Description', key: 'description' },
    { label: 'Long Description', key: 'longDescription' },
    { label: 'BOH Description', key: 'displayDesc' },
    { label: 'Synopsis', key: 'synopsis' },
    { label: 'Status No.', key: 'statusNo' },
    { label: 'Allow Top Type No.', key: 'allowToppingTypeNo' },
    { label: 'Max Topping', key: 'maxTopping' },
    { label: 'Max Free Topping', key: 'maxFreeTopping' },
    { label: 'Service Guaranteed', key: 'serviceGuaranteed' },
    { label: 'Override Kitchen', key: 'overrideKitchenPrint' },
    { label: 'Feature Refs', key: 'featureRefs' },
    { label: 'Mnemonic', key: 'mnemonic' },
    { label: 'Valid on Web', key: 'validOnWeb' },
    { label: 'Web Display Sequence No.', key: 'webDisplay' },
    { label: 'Image ID', key: 'imageId' },
    { label: 'Created By', key: 'createdBy' },
    { label: 'Created Date', key: 'createdDate' },
    { label: 'Updated By', key: 'updatedBy' },
    { label: 'Updated Date', key: 'updatedDate' },
  ];

  const csvData = allProducts.map((product: Product) => ({
    ...product,
    serviceGuaranteed: product.serviceGuaranteed ? 'Y' : 'N',
    overrideKitchenPrint: product.overrideKitchenPrint ? 'Y' : 'N',
    validOnWeb: product.validOnWeb ? 'Y' : 'N',
  }));

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Product' />
      <Page>
        <TableWrapper>
          <Table
            ref={gridRef}
            rowData={allProducts}
            returnedRecords={allProducts?.length || 0}
            height={'100%'}
            rowSelection='single'
            onRowSelected={handleRowSelection}
            columnDefs={[
              {
                minWidth: 48,
                field: '',
                headerName: '',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              { minWidth: 120, field: 'classNo', headerName: 'MOA Class No.' },
              { minWidth: 120, field: 'productNo', headerName: 'MOA Product No.' },
              { minWidth: 120, field: 'productNo', headerName: 'Product No.' },
              { minWidth: 120, field: 'seqNo', headerName: 'Sequence No.' },
              { minWidth: 120, field: 'description', headerName: 'Description' },
              { minWidth: 120, field: 'longDescription', headerName: 'Long Description' },
              { minWidth: 120, field: 'displayDesc', headerName: 'BOH Description' },
              { minWidth: 120, field: 'statusNo', headerName: 'Status No.' },
              { minWidth: 120, field: 'allowToppingTypeNo', headerName: 'Allow Top Type No.' },
              { minWidth: 120, field: 'maxTopping', headerName: 'Max Topping' },
              { minWidth: 120, field: 'maxFreeTopping', headerName: 'Max Free Topping' },
              {
                minWidth: 120,
                field: 'serviceGuaranteed',
                headerName: 'Service Guaranteed',
                valueFormatter: (params) => (params.value === true ? 'Y' : 'N'),
                filterValueGetter: (params: ValueGetterParams) =>
                  params.getValue('serviceGuaranteed') === true ? 'Y' : 'N',
              },
              {
                minWidth: 120,
                field: 'overrideKitchenPrint',
                headerName: 'Override Kitchen',
                valueFormatter: (params) => (params.value === true ? 'Y' : 'N'),
                filterValueGetter: (params: ValueGetterParams) =>
                  params.getValue('overrideKitchenPrint') === true ? 'Y' : 'N',
              },
              { minWidth: 120, field: 'featureRefs', headerName: 'Feature Refs' },
              { minWidth: 120, field: 'mnemonic', headerName: 'Mnemonic' },
              {
                minWidth: 120,
                field: 'validOnWeb',
                headerName: 'Valid on Web',
                valueFormatter: (params) => (params.value === true ? 'Y' : 'N'),
                filterValueGetter: (params: ValueGetterParams) => (params.getValue('validOnWeb') === true ? 'Y' : 'N'),
              },
              {
                minWidth: 120,
                flex: 1,
                field: 'webDisplay',
                headerName: 'Web Display Sequence No.',
                resizable: false,
              },
              {
                minWidth: 80,
                field: 'imageId',
                headerName: 'GUI Image',
                valueFormatter: (params) => (params.value ? 'Y' : 'N'),
                filterValueGetter: (params: ValueGetterParams) => (params.getValue('imageId') ? 'Yes' : 'No'),
              },
            ]}
          />
        </TableWrapper>
        <Buttons>
          <Button
            label='Open'
            variant={Variant.Primary}
            onClick={handleOpenClick}
            disabled={selectedRow === undefined}
          />
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />
          </ThemeProvider>
          <CSVLink
            data={csvData}
            headers={headers}
            filename={`Products_${new Date().toISOString().split('T')[0]}.csv`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Button variant={Variant.Secondary} label='Export to CSV' />
          </CSVLink>
        </Buttons>
      </Page>
    </>
  );
};

export default Products;
